import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { usePerformanceScope } from '@/lib/use-performance-scope'

interface VipRow {
  id: string
  showId: string
  name: string
  description?: string
  pricePence: number
  inventory: number
  allocated: number
  minTierLevel?: number
  perks: string[]
  createdAt: string
  show?: { title: string; date: string }
}

export function VipPage() {
  const qc = useQueryClient()
  const scope = usePerformanceScope()
  const [adding, setAdding] = useState(false)
  const { data, error, isLoading } = useQuery<VipRow[], ApiClientError>({
    queryKey: ['vip', ...scope.queryKeySuffix],
    queryFn: () => artistFetch<VipRow[]>(`/vip${scope.toQueryString()}`),
  })
  const rows = scope.filterByShowIds(data ?? [], (r) => r.showId)

  async function remove(id: string) {
    if (!confirm('Delete this VIP package?')) return
    await artistFetch(`/vip/${id}`, { method: 'DELETE' })
    qc.invalidateQueries({ queryKey: ['vip'] })
  }

  return (
    <div>
      <PageHeader
        title="VIP Experiences"
        subtitle="Meet & greet packages, soundcheck access, premium bundles."
        actions={
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {adding ? 'Cancel' : 'Add package'}
          </button>
        }
      />
      <PerformanceScopeBanner />
      {adding && (
        <AddVipForm
          onCreated={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['vip'] })
          }}
        />
      )}
      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={rows}
          rowKey={(r) => r.id}
          emptyMessage="No VIP packages configured."
          columns={[
            { key: 'name', header: 'Package', render: (r) => <span className="font-medium">{r.name}</span> },
            {
              key: 'show',
              header: 'Show',
              render: (r) => r.show?.title ?? r.showId.slice(0, 8),
            },
            {
              key: 'price',
              header: 'Price',
              align: 'right',
              render: (r) => `£${(r.pricePence / 100).toFixed(2)}`,
            },
            {
              key: 'inventory',
              header: 'Inventory',
              align: 'right',
              render: (r) => `${r.allocated ?? 0} / ${r.inventory}`,
            },
            {
              key: 'tier',
              header: 'Min tier',
              render: (r) => r.minTierLevel ? `Tier ${r.minTierLevel}+` : 'All',
            },
            {
              key: 'perks',
              header: 'Perks',
              render: (r) =>
                r.perks.length > 0 ? (
                  <span className="text-xs text-ovation-muted">{r.perks.join(', ')}</span>
                ) : '—',
            },
            {
              key: 'actions',
              header: '',
              render: (r) => (
                <button
                  type="button"
                  onClick={() => remove(r.id)}
                  className="rounded p-1 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              ),
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}

function AddVipForm({ onCreated }: { onCreated: () => void }) {
  const [showId, setShowId] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState('')
  const [inventory, setInventory] = useState(20)
  const [minTierLevel, setMinTierLevel] = useState('')
  const [perks, setPerks] = useState('')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      await artistFetch('/vip', {
        method: 'POST',
        body: {
          showId,
          name,
          description: description || undefined,
          pricePence: Math.round(Number(price) * 100),
          inventory,
          minTierLevel: minTierLevel ? Number(minTierLevel) : undefined,
          perks: perks.split(',').map((p) => p.trim()).filter(Boolean),
        },
      })
      onCreated()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Show ID</label>
          <input required value={showId} onChange={(e) => setShowId(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="UUID of the show" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Package name</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="Meet & Greet" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Price (major units)</label>
          <input type="number" step="0.01" required value={price} onChange={(e) => setPrice(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="150.00" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Inventory</label>
          <input type="number" min={0} required value={inventory} onChange={(e) => setInventory(Number(e.target.value))} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Min tier level</label>
          <input type="number" min={1} value={minTierLevel} onChange={(e) => setMinTierLevel(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="Optional" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Perks (comma-separated)</label>
          <input value={perks} onChange={(e) => setPerks(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="Photo, signed poster" />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Description</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={submitting} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
        {submitting ? 'Creating...' : 'Create package'}
      </button>
    </form>
  )
}
